import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Sparkles, Lock, LogOut, RefreshCw, Phone, ArrowLeft, Loader2, Inbox } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "scs_admin_token";

const inputCls =
  "w-full bg-[#0C1B24] border border-white/15 focus:border-[#00F0FF] outline-none px-4 py-3.5 font-mukta text-white placeholder:text-slate-500 transition-colors";

const formatDetail = (d) => {
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || "").filter(Boolean).join(" ");
  return "कुछ गड़बड़ हुई — दुबारा प्रयास करें";
};

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("hi-IN", {
      day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [enquiries, setEnquiries] = useState(null);

  const load = useCallback(
    async (t) => {
      try {
        const { data } = await axios.get(`${API}/enquiries`, {
          headers: { Authorization: `Bearer ${t}` },
        });
        setEnquiries(data);
      } catch (e) {
        if (e.response?.status === 401) {
          localStorage.removeItem(TOKEN_KEY);
          setToken("");
        } else {
          setEnquiries([]);
        }
      }
    },
    []
  );

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
    } catch (err) {
      setError(formatDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setEnquiries(null);
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#0C1B24] flex items-center justify-center px-6">
        <div className="w-full max-w-md border border-white/10 bg-[#14242F] p-8 md:p-10">
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 bg-[#00F0FF] flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-[#0C1B24]" />
            </span>
            <p className="font-spacemono text-[10px] tracking-[0.3em] text-[#00F0FF] uppercase">
              Super Cleaner / Admin
            </p>
          </div>
          <h1 className="mt-6 font-khand font-bold text-4xl">Admin Login</h1>
          <p className="mt-2 text-sm text-slate-400 font-mukta">Enquiries देखने के लिए लॉगिन करें।</p>
          <form data-testid="admin-login-form" onSubmit={handleLogin} className="mt-8 space-y-5">
            <input
              data-testid="admin-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="ईमेल"
              className={inputCls}
              required
            />
            <input
              data-testid="admin-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="पासवर्ड"
              className={inputCls}
              required
            />
            {error && (
              <p data-testid="admin-login-error" className="text-sm text-[#FF3B30] font-mukta">
                {error}
              </p>
            )}
            <button
              data-testid="admin-login-btn"
              type="submit"
              disabled={loading}
              className="w-full bg-[#00F0FF] text-[#0C1B24] font-mukta font-semibold px-6 py-3.5 flex items-center justify-center gap-3 hover:bg-white transition-colors disabled:opacity-60"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
              {loading ? "जाँच हो रही है..." : "लॉगिन करें"}
            </button>
          </form>
          <Link
            data-testid="back-to-site-link"
            to="/"
            className="mt-6 inline-flex items-center gap-2 text-sm text-slate-400 hover:text-[#00F0FF] font-mukta transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> वापस website पर
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0C1B24]">
      <header className="border-b border-white/10 backdrop-blur-xl bg-[#0C1B24]/80 sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 h-[72px] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="w-8 h-8 bg-[#00F0FF] flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-[#0C1B24]" />
            </span>
            <span className="font-spacemono font-bold tracking-widest text-sm">
              SUPER CLEANER <span className="text-[#00F0FF]">/ ENQUIRIES</span>
            </span>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="admin-refresh-btn"
              onClick={() => load(token)}
              className="border border-white/20 text-white p-2.5 hover:border-[#00F0FF] hover:text-[#00F0FF] transition-colors"
              aria-label="Refresh"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
            <button
              data-testid="admin-logout-btn"
              onClick={logout}
              className="border border-white/20 text-white px-4 py-2.5 flex items-center gap-2 text-sm font-mukta hover:border-[#FF3B30] hover:text-[#FF3B30] transition-colors"
            >
              <LogOut className="w-4 h-4" /> लॉगआउट
            </button>
          </div>
        </div>
      </header>

      <main data-testid="admin-dashboard" className="max-w-[1400px] mx-auto px-6 md:px-12 py-12">
        <p className="font-spacemono text-[10px] tracking-[0.3em] text-[#00F0FF] uppercase">
          [ Website Enquiries ]
        </p>
        <h1 className="mt-3 font-khand font-bold text-4xl md:text-5xl">
          सभी Enquiries{" "}
          <span data-testid="enquiry-count" className="text-[#00F0FF]">
            {enquiries ? `(${enquiries.length})` : ""}
          </span>
        </h1>

        {enquiries === null ? (
          <p data-testid="enquiries-loading" className="mt-16 flex items-center gap-3 text-slate-400 font-mukta">
            <Loader2 className="w-5 h-5 animate-spin" /> लोड हो रहा है...
          </p>
        ) : enquiries.length === 0 ? (
          <div data-testid="enquiries-empty" className="mt-16 border border-white/10 bg-[#14242F] p-12 text-center">
            <Inbox className="w-10 h-10 text-slate-500 mx-auto" />
            <p className="mt-4 text-slate-400 font-mukta">अभी कोई enquiry नहीं आई है।</p>
          </div>
        ) : (
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4">
            {enquiries.map((q, i) => (
              <div
                key={q.id}
                data-testid={`enquiry-card-${i}`}
                className="border border-white/10 bg-[#14242F] p-6 hover:border-[#00F0FF]/40 transition-colors"
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="font-khand font-semibold text-2xl leading-tight">{q.name}</h3>
                    <p className="mt-1 font-spacemono text-[10px] tracking-[0.2em] text-slate-500 uppercase">
                      {formatDate(q.created_at)}
                    </p>
                  </div>
                  <span className="shrink-0 bg-[#00F0FF]/10 text-[#00F0FF] border border-[#00F0FF]/30 px-3 py-1 text-xs font-mukta">
                    {q.service}
                  </span>
                </div>
                <a
                  data-testid={`enquiry-phone-${i}`}
                  href={`tel:+91${String(q.phone).replace(/\D/g, "").slice(-10)}`}
                  className="mt-4 inline-flex items-center gap-2 text-lg font-mukta text-white hover:text-[#00F0FF] transition-colors"
                >
                  <Phone className="w-4 h-4 text-[#00F0FF]" /> {q.phone}
                </a>
                {q.address && <p className="mt-2 text-sm text-slate-400 font-mukta">पता: {q.address}</p>}
                {q.message && <p className="mt-2 text-sm text-slate-400 font-mukta">संदेश: {q.message}</p>}
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
